import React, { useState } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../lib/firebase';
import { Lock, Mail, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';

export const RegisterForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('rider');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!name.trim()) {
      setError('Bitte geben Sie Ihren Namen ein');
      setLoading(false);
      return;
    }

    if (!privacyAccepted) {
      setError('Bitte akzeptieren Sie die Datenschutzbestimmungen');
      setLoading(false);
      return;
    }

    try {
      // Create the user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;

      if (!user) {
        throw new Error('Benutzer konnte nicht erstellt werden');
      }

      // Send verification email
      await sendEmailVerification(user);

      // Check auto-approve settings for non-manager roles
      let approved = false;
      if (role !== 'manager') {
        try {
          const settingsDoc = await getDoc(doc(db, 'settings', 'userPermissions'));
          const settings = settingsDoc.data();
          approved = settings?.autoApproveUsers || false;
        } catch (error) {
          console.error('Error checking settings:', error);
          approved = false;
        }
      }

      // Create the user document
      await setDoc(doc(db, 'users', user.uid), {
        email,
        name: name.trim(),
        role,
        approved,
        emailVerified: false,
        privacyAccepted: true,
        privacyAcceptedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      });

      // Sign out until email is verified
      await auth.signOut();
      setVerificationSent(true);

      // Store email and role in session storage for the verification prompt
      sessionStorage.setItem('emailForVerification', email);
      sessionStorage.setItem('roleForVerification', role);

      // Navigate to verification prompt
      navigate('/verify-email', { 
        state: { email, role },
        replace: true 
      });
    } catch (err: any) {
      console.error('Registration error:', err);
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError('Diese E-Mail-Adresse wird bereits verwendet');
          break;
        case 'auth/invalid-email':
          setError('Ungültige E-Mail-Adresse');
          break;
        case 'auth/weak-password':
          setError('Das Passwort muss mindestens 6 Zeichen lang sein');
          break;
        case 'auth/operation-not-allowed':
          setError('Die E-Mail/Passwort Registrierung ist deaktiviert');
          break;
        case 'permission-denied':
          setError('Keine Berechtigung zum Erstellen eines Benutzerkontos');
          break;
        default:
          setError(err.message || 'Ein Fehler ist bei der Registrierung aufgetreten');
      }
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="text-center px-4 sm:px-0">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4">
          E-Mail-Bestätigung erforderlich
        </h2>
        <p className="text-base sm:text-sm text-gray-600 dark:text-gray-400 mb-6">
          Wir haben Ihnen eine E-Mail an <strong>{email}</strong> gesendet. 
          Bitte klicken Sie auf den Link in der E-Mail, um Ihre Registrierung abzuschließen.
        </p>
        {role === 'manager' ? (
          <span className="block mt-2 text-sm text-gray-600 dark:text-gray-400">
            Nach der E-Mail-Bestätigung wird Ihr Konto von einem Administrator freigeschaltet.
          </span>
        ) : (
          <span className="block mt-2 text-sm text-gray-600 dark:text-gray-400">
            Sie können sich nach der E-Mail-Bestätigung anmelden.
          </span>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
          Keine E-Mail erhalten? Überprüfen Sie Ihren Spam-Ordner oder{' '}
          <button
            onClick={() => window.location.reload()}
            className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            versuchen Sie es erneut
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto px-4 sm:px-0">
      <ThemeToggle />
      <h2 className="text-xl sm:text-2xl font-bold text-center text-gray-900 dark:text-white mb-6 sm:mb-8">
        Konto erstellen
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Name
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
              placeholder="Max Mustermann"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            E-Mail
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
              placeholder="ihre@email.de"
              required
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Passwort
          </label>
          <div className="mt-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-base sm:text-sm"
              placeholder="••••••••"
              required
              minLength={6}
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Rolle
          </label>
          <select
            id="role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-base sm:text-sm py-2 sm:py-1.5"
            disabled={loading}
          >
            <option value="rider">Reiter (Pferdebesitzer)</option>
            <option value="co-rider">Mitreiter</option>
            <option value="manager">Verwalter</option>
          </select>
          {role === 'co-rider' && (
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Als Mitreiter können Sie nach der Registrierung Zugriff auf Pferde anderer Reiter beantragen.
            </p>
          )}
        </div>

        <div className="relative flex items-start">
          <div className="flex h-5 items-center">
            <input
              id="privacy"
              type="checkbox"
              checked={privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
              required
            />
          </div>
          <div className="ml-3 text-sm">
            <label htmlFor="privacy" className="font-medium text-gray-700 dark:text-gray-300">
              Datenschutzbestimmungen
            </label>
            <p className="text-gray-500 dark:text-gray-400">
              Ich habe die{' '}
              <Link
                to="/datenschutz"
                target="_blank"
                className="text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                Datenschutzerklärung
              </Link>
              {' '}gelesen und akzeptiere diese.
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">{error}</h3>
              </div>
            </div>
          </div>
        )}

        <div className="pt-2">
          <button
            type="submit"
            disabled={loading || !privacyAccepted}
            className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-gray-800 ${
              (loading || !privacyAccepted) ? 'opacity-75 cursor-not-allowed' : ''
            }`}
          >
            {loading ? 'Registrierung läuft...' : 'Registrieren'}
          </button>
        </div>

        <div className="text-sm text-center pt-4">
          <span className="text-gray-600 dark:text-gray-400">Bereits ein Konto? </span>
          <Link to="/" className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
            Hier anmelden
          </Link>
        </div>
      </form>
    </div>
  );
};
